import { useRef, useState } from "react";
import { Sparkles, Send, User, X } from "lucide-react";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function AzureAssistant({ context, onClose }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your Azure & Entra tutor. Ask me about RBAC vs Entra roles, resource groups, locks, `az` CLI syntax, or how to read an Azure error — I'll guide you." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const scrollDown = () => setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 30);

  const send = async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    scrollDown();
    try {
      const res = await fetch(`${API}/azure/assistant/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ message: msg, context: context || "" }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = dec.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + chunk }; return c; });
        scrollDown();
      }
    } catch {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "The assistant is unavailable right now." }; return c; });
    } finally { setStreaming(false); scrollDown(); }
  };

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-sm z-40 bg-card border-l border-border flex flex-col shadow-2xl" data-testid="az-assistant">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <Sparkles className="h-4 w-4 text-[#0078d4]" />
        <span className="font-display font-bold text-sm">Azure Assistant</span>
        <Button size="icon" variant="ghost" className="ml-auto h-7 w-7" onClick={onClose}><X className="h-4 w-4" /></Button>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`az-assistant-msg-${m.role}`}>
            <div className={`h-7 w-7 rounded-md grid place-items-center shrink-0 ${m.role === "user" ? "bg-secondary" : "bg-[#0078d4] text-white"}`}>
              {m.role === "user" ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
            </div>
            <div className={`rounded-lg px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "bg-secondary" : "bg-accent/60 border border-border"}`}>
              {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-border flex gap-2">
        <Input data-testid="az-assistant-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about Azure…" disabled={streaming} />
        <Button size="icon" onClick={send} disabled={streaming} data-testid="az-assistant-send" className="bg-[#0078d4] hover:bg-[#106ebe] text-white"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
